
import type { HeightField } from "../heightfield";
import { GlError, checkGlError } from "./context";

export interface TextureArray {
  readonly texture: WebGLTexture;
  readonly layers: number;
  readonly size: number;
}

export function createTextureArray(
  gl: WebGL2RenderingContext,
  strip: ImageBitmap | HTMLImageElement,
  layerCount: number,
): TextureArray {
  const width = strip.width;
  const height = strip.height;
  if (width <= 0 || height !== width * layerCount) {
    throw new GlError(
      `terrain atlas is ${width}x${height}, expected ${layerCount} layers of ${width}x${width}`,
    );
  }

  const scratch = document.createElement("canvas");
  scratch.width = width;
  scratch.height = width;
  const ctx = scratch.getContext("2d", { willReadFrequently: true });
  if (ctx === null) throw new GlError("no Canvas2D context to decode the terrain atlas with");

  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the terrain atlas texture");
  gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
  // Flipping y would flip the strip as a whole, which reorders the layers as well
  // as the rows inside them. The layers are addressed by index, so the order is
  // load-bearing and this stays off.
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  // Reserve the complete array first. Each exact source rectangle is copied to
  // its matching z layer below, so the top-to-bottom atlas order stays the
  // layer order `TileMap` uses.
  gl.texImage3D(
    gl.TEXTURE_2D_ARRAY,
    0,
    gl.RGBA8,
    width,
    width,
    layerCount,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );
  for (let layer = 0; layer < layerCount; layer++) {
    ctx.clearRect(0, 0, width, width);
    ctx.drawImage(strip, 0, layer * width, width, width, 0, 0, width, width);
    const pixels = ctx.getImageData(0, 0, width, width).data;
    gl.texSubImage3D(
      gl.TEXTURE_2D_ARRAY,
      0,
      0,
      0,
      layer,
      width,
      width,
      1,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels,
    );
  }

  // Mipmaps are not optional here: at the game's scale a 256 px texture covers
  // about 170 screen pixels, so every frame samples below level zero, and without
  // a chain that is aliasing rather than ground.
  gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.REPEAT);

  // Drained once, here, rather than per frame: an upload that the driver refused
  // -- a size past MAX_ARRAY_TEXTURE_LAYERS, an out-of-memory -- otherwise shows
  // up as terrain that is uniformly black with nothing in the console.
  checkGlError(gl, "terrain atlas upload");

  return { texture, layers: layerCount, size: width };
}

/** Fetch and decode the atlas. `createImageBitmap` keeps the decode off the main
 *  thread; the `<img>` path is the fallback for browsers without it. */
export async function loadImage(url: string): Promise<ImageBitmap | HTMLImageElement> {
  const response = await fetch(url);
  if (!response.ok) throw new GlError(`${url}: HTTP ${response.status}`);
  const blob = await response.blob();
  if (typeof createImageBitmap === "function") return createImageBitmap(blob);

  const objectUrl = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new GlError(`${url}: image failed to decode`));
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function createCoverageTexture(
  gl: WebGL2RenderingContext,
  image: ImageBitmap | HTMLImageElement,
): WebGLTexture {
  const width = image.width;
  const height = image.height;
  if (width <= 0 || height <= 0) {
    throw new GlError(`shadow map is ${width}x${height}`);
  }

  const scratch = document.createElement("canvas");
  scratch.width = width;
  scratch.height = height;
  const ctx = scratch.getContext("2d", { willReadFrequently: true });
  if (ctx === null) throw new GlError("no Canvas2D context to decode the shadow map with");
  ctx.drawImage(image, 0, 0);
  const rgba = ctx.getImageData(0, 0, width, height).data;

  // Canvas2D has no single-channel surface, so the decode arrives as RGBA and
  // the red channel is taken. Uploading the RGBA directly and sampling `.r` would
  // work and would cost four times the memory for the same answer.
  const red = new Uint8Array(width * height);
  for (let i = 0; i < red.length; i++) red[i] = rgba[i * 4];

  return uploadCoverage(gl, red, width, height);
}

export function createFullyLitTexture(gl: WebGL2RenderingContext): WebGLTexture {
  return uploadCoverage(gl, new Uint8Array([0xff]), 1, 1);
}

export function createNormalTexture(
  gl: WebGL2RenderingContext,
  image: ImageBitmap | HTMLImageElement,
): WebGLTexture {
  const width = image.width;
  const height = image.height;
  if (width <= 0 || height <= 0) throw new GlError(`normal map is ${width}x${height}`);

  const scratch = document.createElement("canvas");
  scratch.width = width;
  scratch.height = height;
  const ctx = scratch.getContext("2d", { willReadFrequently: true });
  if (ctx === null) throw new GlError("no Canvas2D context to decode the normal map with");
  ctx.drawImage(image, 0, 0);
  const rgba = ctx.getImageData(0, 0, width, height).data;

  const rgb = new Uint8Array(width * height * 3);
  for (let i = 0, j = 0; i < rgb.length; i += 3, j += 4) {
    rgb[i] = rgba[j];
    rgb[i + 1] = rgba[j + 1];
    rgb[i + 2] = rgba[j + 2];
  }

  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the normal map texture");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  // RGB rows are three bytes wide and 513 texels of them is not a multiple of
  // four; left at the default the driver pads each row and every row past the
  // first reads shifted, which is a normal map that leans a little more every
  // scanline down.
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB8, width, height, 0, gl.RGB, gl.UNSIGNED_BYTE, rgb);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  // Linear, no mip: the bake is already coarser than the shadow map (one texel
  // per two world metres) and the whole point of a heightfield is that it does
  // not have a repeating pattern to alias against, so there is nothing here for
  // a mip chain to fix.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  // CLAMP_TO_EDGE for the same reason as the shadow map: this is the world
  // exactly once, and wrapping would light the far edge with this edge's slope.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  checkGlError(gl, "normal map upload");
  return texture;
}

/** Flat "facing the camera" normal, for when the bake is missing. Encodes
 *  (0, 0, 1) -- straight up -- so the lighting term this feeds reduces to a
 *  constant and the terrain looks exactly as it did before the bake existed,
 *  rather than as if every cell were a wall. */
export function createFlatNormalTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the flat normal texture");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGB8,
    1,
    1,
    0,
    gl.RGB,
    gl.UNSIGNED_BYTE,
    new Uint8Array([128, 128, 255]),
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  checkGlError(gl, "flat normal texture upload");
  return texture;
}

export function createHeightTexture(
  gl: WebGL2RenderingContext,
  field: HeightField["texture"],
): WebGLTexture {
  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the height field texture");

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  // Rows are two bytes wide per texel; a width that is not a multiple of two
  // would otherwise be padded by the default alignment of four, and every row
  // past the first would read shifted -- the same failure `createNormalTexture`
  // guards against for its three-byte rows.
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 2);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.R16UI,
    field.width,
    field.height,
    0,
    gl.RED_INTEGER,
    gl.UNSIGNED_SHORT,
    field.samples,
  );
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  // NEAREST is not a style choice here: the WebGL2 spec makes LINEAR an
  // incomplete-texture error on an integer internal format, so this is the only
  // legal filter. CLAMP_TO_EDGE matches `HeightField.at`'s own edge handling --
  // it clamps the sample indices rather than wrapping past the map's border.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  checkGlError(gl, "height field upload");
  return texture;
}

function uploadCoverage(
  gl: WebGL2RenderingContext,
  pixels: Uint8Array,
  width: number,
  height: number,
): WebGLTexture {
  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the shadow map texture");

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  // UNPACK_ALIGNMENT is global state and R8 rows are rarely a multiple of four;
  // leaving it at 1 would silently misalign the next RGBA upload's rows.
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);

  checkGlError(gl, "shadow map upload");
  return texture;
}
